/**
 * ChipMulti — multi-select control with selected Chips + expandable checkbox list.
 *
 * Selected items render as removable Chips above a toggle button. Clicking
 * the toggle expands a checkbox list of all options; each checkbox toggles
 * inclusion in `value`.
 *
 * Options are generic: pass `{ id, name, description?, badge? }`. The `badge`
 * slot lets feature code render scope or provenance chips alongside the name
 * without coupling this primitive to a domain shape.
 */

import { useId, useMemo, useState, type Key, type ReactNode } from 'react';
import { Chip } from '@/components/Chip/Chip';
import { t } from '@/lib/i18n';
import styles from './ChipMulti.module.css';

export interface ChipMultiOption<Id extends Key> {
  id: Id;
  name: string;
  description?: string;
  /** Optional badge rendered to the right of the name in the option row. */
  badge?: ReactNode;
}

export interface ChipMultiProps<Id extends Key> {
  label: string;
  hint?: string;
  options: readonly ChipMultiOption<Id>[];
  value: readonly Id[];
  onChange: (next: Id[]) => void;
  isLoading?: boolean;
  /** Label shown inside the option list when `options` is empty. */
  emptyLabel?: string;
}

export function ChipMulti<Id extends Key>({
  label,
  hint,
  options,
  value,
  onChange,
  isLoading,
  emptyLabel,
}: ChipMultiProps<Id>) {
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const listboxId = `${labelId}-list`;

  const selectedSet = useMemo(() => new Set(value), [value]);
  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.name.localeCompare(b.name)),
    [options],
  );

  function toggle(id: Id) {
    if (selectedSet.has(id)) {
      onChange(value.filter((s) => s !== id));
    } else {
      onChange([...value, id]);
    }
  }

  function remove(id: Id) {
    onChange(value.filter((s) => s !== id));
  }

  const selectedItems = value.map((id) => {
    const match = options.find((o) => o.id === id);
    return { id, name: match?.name ?? String(id) };
  });

  return (
    <div className={styles.field}>
      <span id={labelId} className={styles.label}>
        {label}
      </span>

      {selectedItems.length > 0 ? (
        <div className={styles.chips}>
          {selectedItems.map((it) => (
            <Chip
              key={it.id}
              variant="neutral"
              size="md"
              onRemove={() => remove(it.id)}
              removeLabel={t('Remove {name}', { name: it.name })}
            >
              {it.name}
            </Chip>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={labelId}
        onClick={() => setOpen((o) => !o)}
        disabled={isLoading}
      >
        {isLoading ? t('Loading…') : selectedItems.length > 0 ? t('Add or remove') : t('Choose…')}
      </button>

      {open && !isLoading ? (
        <ul id={listboxId} className={styles.list} aria-labelledby={labelId}>
          {sortedOptions.length === 0 ? (
            <li className={styles.empty}>{emptyLabel ?? t('Nothing available yet.')}</li>
          ) : (
            sortedOptions.map((opt) => {
              const checked = selectedSet.has(opt.id);
              return (
                <li key={opt.id}>
                  <label className={styles.option}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt.id)}
                      className={styles.checkbox}
                    />
                    <span className={styles.optionText}>
                      <span className={styles.optionNameLine}>
                        <span className={styles.optionName}>{opt.name}</span>
                        {opt.badge ?? null}
                      </span>
                      {opt.description ? (
                        <span className={styles.optionDesc}>{opt.description}</span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })
          )}
        </ul>
      ) : null}

      {hint ? <div className={styles.hint}>{hint}</div> : null}
    </div>
  );
}
